import { Segmented } from 'antd'
import { useState, type ReactNode } from 'react'

import { cx } from '@/Ship/Helpers/cssClass'
import { useMobileMode } from '@/Ship/Hooks/useMobileMode'

import styles from './SettingsShell.module.css'

export interface SettingsShellSection {
    key: string
    label: string
    icon?: ReactNode
    content: ReactNode
}

interface SettingsShellProps {
    title: string
    subtitle?: string
    /** Вміст кружка в шапці: фото/ініціали користувача або іконка розділу */
    avatar?: ReactNode
    sections: SettingsShellSection[]
    /** Ключ розділу за замовчуванням; інакше перший */
    defaultSection?: string
    /** Підпис картки й навігації для скрінрідерів; за замовчуванням title */
    ariaLabel?: string
}

/**
 * Оболонка сторінки налаштувань (профіль, налаштування порталу): картка в стилі
 * картки колекції з шапкою, лівою навігацією між розділами (на мобільному —
 * Segmented зверху) і робочою частиною. Вміст розділу перемонтовується при перемиканні.
 */
export const SettingsShell = ({ title, subtitle, avatar, sections, defaultSection, ariaLabel }: SettingsShellProps) => {
    const isMobile = useMobileMode(991)
    const [active, setActive] = useState<string>(defaultSection ?? sections[0]?.key ?? '')
    const current = sections.find((section) => section.key === active) ?? sections[0]
    const label = ariaLabel ?? title

    const renderIcon = (icon: ReactNode) =>
        icon ? (
            <span className={cx(styles, 'navIcon')} aria-hidden="true">
                {icon}
            </span>
        ) : null

    return (
        <div className={cx(styles, 'page')}>
            <section className={cx(styles, 'card')} aria-label={label}>
                <header className={cx(styles, 'header')}>
                    {avatar !== undefined && (
                        <div className={cx(styles, 'headerAvatar')} aria-hidden="true">
                            {avatar}
                        </div>
                    )}
                    <div className={cx(styles, 'headerMain')}>
                        <h1 className={cx(styles, 'title')}>{title}</h1>
                        {subtitle && <div className={cx(styles, 'subtitle')}>{subtitle}</div>}
                    </div>
                </header>

                <div className={cx(styles, 'body')}>
                    {isMobile ? (
                        <div className={cx(styles, 'navMobile')}>
                            <Segmented<string>
                                value={active}
                                onChange={setActive}
                                options={sections.map((section) => ({
                                    value: section.key,
                                    label: section.label,
                                    icon: renderIcon(section.icon),
                                }))}
                            />
                        </div>
                    ) : (
                        <nav className={cx(styles, 'nav')} aria-label={label}>
                            {sections.map((section) => {
                                const isActive = section.key === current?.key
                                return (
                                    <button
                                        key={section.key}
                                        type="button"
                                        className={`${cx(styles, 'navItem')} ${isActive ? cx(styles, 'navItemActive') : ''}`}
                                        aria-current={isActive ? 'page' : undefined}
                                        onClick={() => setActive(section.key)}
                                    >
                                        {renderIcon(section.icon)}
                                        <span>{section.label}</span>
                                    </button>
                                )
                            })}
                        </nav>
                    )}

                    {current && (
                        <div className={cx(styles, 'content')} key={current.key}>
                            {current.content}
                        </div>
                    )}
                </div>
            </section>
        </div>
    )
}
